// Customer support DTOs. Backs the dashboard /support page (in-app
// ticketing). The customer-side surface is small: file a ticket, list +
// view threads, reply, acknowledge resolution. Staff-side transitions
// happen out-of-band (psql) until the admin UI lands.
import { Type } from '@sinclair/typebox';
// ────────── status / priority unions ──────────────────────────────────
//
// These mirror the CHECK constraints in migration 0019. Kept as `as
// const` arrays so callers can iterate (e.g. the dashboard's priority
// <Select>) without re-listing the literals.
export const SUPPORT_STATUSES = [
    'open',
    'in_progress',
    'waiting_customer',
    'resolved',
    'closed',
];
export const SupportStatusSchema = Type.Union(SUPPORT_STATUSES.map((s) => Type.Literal(s)), { $id: 'SupportStatus' });
export const SUPPORT_PRIORITIES = ['low', 'normal', 'high', 'urgent'];
export const SupportPrioritySchema = Type.Union(SUPPORT_PRIORITIES.map((p) => Type.Literal(p)), { $id: 'SupportPriority' });
// Plan tiers as the support route understands them. Not authoritative —
// plan_limits.code is the source of truth — but the gate function in the
// route narrows to this set, and the dashboard's priority <Select> uses
// it to enable / disable options.
export const SUPPORT_PRIORITY_BY_PLAN = {
    free: ['low', 'normal'],
    starter: ['low', 'normal'],
    pro: ['low', 'normal', 'high', 'urgent'],
};
// ────────── ticket DTO ────────────────────────────────────────────────
//
// Returned by GET /v1/support and embedded inside the GET /v1/support/:id
// response. `tenantId` is included even though the dashboard already
// knows it — keeps the shape uniform with Agent / Conversation summaries
// and lets future admin views consume the same DTO without forking.
export const SupportTicketSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    tenantId: Type.String({ format: 'uuid' }),
    // Clerk user id. TEXT, not uuid — Clerk's ids are opaque strings.
    createdBy: Type.String({ minLength: 1, maxLength: 128 }),
    subject: Type.String({ minLength: 1, maxLength: 200 }),
    status: SupportStatusSchema,
    priority: SupportPrioritySchema,
    planCodeAtCreation: Type.String({ minLength: 1, maxLength: 32 }),
    lastMessageAt: Type.String({ format: 'date-time' }),
    resolvedAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    closedAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
}, { $id: 'SupportTicket', additionalProperties: false });
export const SupportMessageSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    ticketId: Type.String({ format: 'uuid' }),
    authorKind: Type.Union([Type.Literal('customer'), Type.Literal('staff')]),
    // Null for system-generated messages (none yet, but reserved).
    authorId: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    body: Type.String({ minLength: 1, maxLength: 10_000 }),
    createdAt: Type.String({ format: 'date-time' }),
}, { $id: 'SupportMessage', additionalProperties: false });
// ────────── list + detail responses ───────────────────────────────────
export const SupportListResponseSchema = Type.Object({
    tickets: Type.Array(SupportTicketSchema),
}, { $id: 'SupportListResponse', additionalProperties: false });
export const SupportTicketDetailSchema = Type.Object({
    ticket: SupportTicketSchema,
    messages: Type.Array(SupportMessageSchema),
}, { $id: 'SupportTicketDetail', additionalProperties: false });
// ────────── inputs ────────────────────────────────────────────────────
export const CreateSupportTicketInputSchema = Type.Object({
    subject: Type.String({ minLength: 1, maxLength: 200 }),
    // Becomes the first support_messages row (author_kind='customer').
    description: Type.String({ minLength: 1, maxLength: 10_000 }),
    // Plan-gated server-side. Free + Starter capped at 'normal'; Pro
    // unlocks the full set. Omitting defaults to 'normal'.
    priority: Type.Optional(SupportPrioritySchema),
}, { $id: 'CreateSupportTicketInput', additionalProperties: false });
export const AddSupportMessageInputSchema = Type.Object({
    body: Type.String({ minLength: 1, maxLength: 10_000 }),
}, { $id: 'AddSupportMessageInput', additionalProperties: false });
// PATCH /v1/support/:id — customer-side transitions only. Today the
// only valid customer transition is resolved → closed.
export const UpdateSupportTicketInputSchema = Type.Object({
    acknowledgeResolution: Type.Optional(Type.Literal(true)),
}, { $id: 'UpdateSupportTicketInput', additionalProperties: false });
//# sourceMappingURL=support.js.map